import { getBoxById, getContainerByColumnId } from '../store/lookups';
import { findTitleLevel1ContainerRange } from '../utils/titleRange';
import { variableLockKeysInBox } from './variableRefs';
import {
  CONFIGURATOR_LOCK_ID,
  SECTION_LOADING_LOCK_ID,
  RESTORE_LOCK_ID,
  parsePreviewLockId,
  previewLockId,
} from './lockIds';

// Verrous structurels dérivés des verrous de box : un container hébergeant
// une box éditée par un co-éditeur ne doit être ni supprimé, ni déplacé, ni
// voir ses colonnes modifiées. Fonctions pures sur l'état du store (pas
// d'import du store : appelées depuis ses actions avec get()).
// Les clés de verrou non-box (variables 'custom.<id>', ids 'reserved:*')
// ne matchent aucune box → ignorées naturellement.

// Détenteur (autre que soi) d'un verrou sur une box hébergée par le container.
export const containerLockHolder = (state, containerId) => {
  const { boxLocks, boxes, masks, containers, user } = state;
  const userId = user?.id;
  for (const lockedId of Object.keys(boxLocks)) {
    const holder = boxLocks[lockedId];
    if (holder.id === userId) continue;
    const box = getBoxById(boxes, masks, lockedId);
    if (!box?.columnId) continue;
    if (getContainerByColumnId(containers, box.columnId)?.id === containerId) {
      return holder;
    }
  }
  return null;
};

// Détenteur (autre que soi) du verrou de chargement de section : pendant
// qu'un co-éditeur injecte une section, les opérations structurelles locales
// sont gelées (les frappes texte restent permises).
export const sectionLoadingHolder = (state) => {
  const lock = state.boxLocks[SECTION_LOADING_LOCK_ID];
  return lock && lock.id !== state.user?.id ? lock : null;
};

// Détenteur (autre que soi) du verrou configurateur : la configuration
// (marges, styles...) recalcule la mise en page de toutes les box → aucune
// édition de box/variable n'est permise pendant qu'un co-éditeur configure.
export const configuratorHolder = (state) => {
  const lock = state.boxLocks[CONFIGURATOR_LOCK_ID];
  return lock && lock.id !== state.user?.id ? lock : null;
};

// Détenteur (autre que soi) du verrou de restauration de version : le content
// entier est en train d'être remplacé, toute édition serait perdue/aberrante.
export const restoreHolder = (state) => {
  const lock = state.boxLocks[RESTORE_LOCK_ID];
  return lock && lock.id !== state.user?.id ? lock : null;
};

// Gel global de l'édition par un co-éditeur : configuration, chargement de
// section ou restauration de version en cours. Bloque la création de
// box/container (opération structurelle qui recalcule la mise en page).
// Retourne { holder, message } ou null.
export const editFreezeHolder = (state) => {
  if (state.sessionRevoked) {
    return { holder: null, message: 'Ce document est ouvert dans un autre onglet ou appareil' };
  }
  const restore = restoreHolder(state);
  if (restore) return { holder: restore, message: `${restore.name} restaure une version` };
  const config = configuratorHolder(state);
  if (config) return { holder: config, message: `${config.name} configure le document` };
  const section = sectionLoadingHolder(state);
  if (section) return { holder: section, message: `${section.name} charge une section` };
  return null;
};

// Premier verrou détenu par un co-éditeur, toutes clés confondues (box,
// variable, section, configurateur) : le configurateur ne s'ouvre que si
// personne n'édite quoi que ce soit.
export const anyRemoteLockHolder = (state) => {
  const { boxLocks, user } = state;
  const userId = user?.id;
  const lockedId = Object.keys(boxLocks).find(
    (key) => boxLocks[key].id !== userId
  );
  return lockedId ? boxLocks[lockedId] : null;
};

// Pré-check d'ouverture/navigation d'aperçu : la section ancrée sur
// `startContainerId` est-elle indisponible ? Retourne { holder, reason } si un
// co-éditeur édite une box (reason 'box') ou une variable embarquée (reason
// 'variable') de la section, ou si l'aperçu de cette même section est déjà
// ouvert ailleurs (reason 'duplicate'). null sinon. Pur.
export const previewOpenBlockedBy = (state, startContainerId) => {
  const { boxLocks, boxes, containers, columns, user } = state;
  const userId = user?.id;
  if (!startContainerId) return null;

  const { boxesIncluded, containersIncluded } = findTitleLevel1ContainerRange({
    containers,
    boxes,
    columns,
    startContainerId,
  });
  if (!boxesIncluded.length) return null;

  const anchorId = containersIncluded[0]?.id || startContainerId;
  const previewHolder = boxLocks[previewLockId(anchorId)];
  if (previewHolder && previewHolder.id !== userId) {
    return { holder: previewHolder, reason: 'duplicate' };
  }

  for (const box of boxesIncluded) {
    const lock = boxLocks[box.id];
    if (lock && lock.id !== userId) return { holder: lock, reason: 'box' };
    const lockedVarKey = variableLockKeysInBox(box).find(
      (key) => boxLocks[key] && boxLocks[key].id !== userId
    );
    if (lockedVarKey) {
      return { holder: boxLocks[lockedVarKey], reason: 'variable' };
    }
  }
  return null;
};

// Détenteur d'un verrou sur le container parent d'une des colonnes visées.
export const columnsLockHolder = (state, columnsIds = []) => {
  for (const columnId of columnsIds) {
    const container = getContainerByColumnId(state.containers, columnId);
    if (container) {
      const holder = containerLockHolder(state, container.id);
      if (holder) return holder;
    }
  }
  return null;
};

// --- Verrou d'aperçu de section -------------------------------------------
// Les clés 'reserved:preview:<anchorContainerId>' détenues par des co-éditeurs
// verrouillent toute la plage de leur titre niveau 1. On dérive, une fois par
// changement d'état pertinent, deux Map (box / container) → détenteur. La
// dérivation rejoue findTitleLevel1ContainerRange (même calcul que l'aperçu)
// pour chaque clé preview distante.

let previewMapCache = null;
let previewMapDeps = null;

const depsUnchanged = (a, b) =>
  a && b && a.length === b.length && a.every((v, i) => v === b[i]);

// { boxMap: Map<boxId, holder>, containerMap: Map<containerId, holder> }
// Détenteurs autres que soi uniquement. Memo mono-emplacement sur les
// références des collections (immer renvoie des refs neuves à chaque mutation).
export const previewLockedBoxMap = (state) => {
  const { boxLocks, boxes, containers, columns, masks, user } = state;
  const userId = user?.id;
  const deps = [boxLocks, boxes, containers, columns, masks, userId];
  if (previewMapCache && depsUnchanged(deps, previewMapDeps)) {
    return previewMapCache;
  }

  const boxMap = new Map();
  const containerMap = new Map();

  for (const key of Object.keys(boxLocks)) {
    const anchorContainerId = parsePreviewLockId(key);
    if (!anchorContainerId) continue;
    const holder = boxLocks[key];
    if (!holder || holder.id === userId) continue;

    const { boxesIncluded, containersIncluded } = findTitleLevel1ContainerRange({
      containers,
      boxes,
      columns,
      startContainerId: anchorContainerId,
    });
    for (const box of boxesIncluded) {
      if (!boxMap.has(box.id)) boxMap.set(box.id, holder);
    }
    for (const container of containersIncluded) {
      if (!containerMap.has(container.id)) containerMap.set(container.id, holder);
    }
  }

  previewMapCache = { boxMap, containerMap };
  previewMapDeps = deps;
  return previewMapCache;
};

// Détenteur (autre que soi) de l'aperçu d'une section contenant cette box.
export const previewLockHolder = (state, boxId) =>
  previewLockedBoxMap(state).boxMap.get(boxId) || null;

// Détenteur (autre que soi) de l'aperçu d'une section contenant ce container.
export const previewContainerLockHolder = (state, containerId) =>
  previewLockedBoxMap(state).containerMap.get(containerId) || null;

// Détenteur de l'aperçu d'une section liée à cette variable : soit la variable
// box elle-même appartient à une section aperçue, soit une box de section
// aperçue embarque cette variable (clé 'custom.<id>' ou id de variable box).
export const previewVariableLockHolder = (state, variableKey) => {
  const { boxMap } = previewLockedBoxMap(state);
  if (boxMap.size === 0) return null;
  const direct = boxMap.get(variableKey);
  if (direct) return direct;
  const { boxes, masks } = state;
  for (const [boxId, holder] of boxMap) {
    const box = getBoxById(boxes, masks, boxId);
    if (box && variableLockKeysInBox(box).includes(variableKey)) return holder;
  }
  return null;
};

// Détenteur (autre que soi) de l'aperçu d'une section embarquant l'une de ces
// colonnes (via leur container parent).
export const previewColumnsLockHolder = (state, columnsIds = []) => {
  const { containerMap } = previewLockedBoxMap(state);
  if (containerMap.size === 0) return null;
  for (const columnId of columnsIds) {
    const container = getContainerByColumnId(state.containers, columnId);
    if (container && containerMap.has(container.id)) {
      return containerMap.get(container.id);
    }
  }
  return null;
};
